import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { reportWarning } from 'shared/sentry';

const SwalReact = withReactContent(Swal);

// Default configuration for the app
const DEFAULT_CONFIG = {
  confirmButtonColor: '#F334BE',
  confirmButtonText: 'OK',
  allowEscapeKey: false,
  allowOutsideClick: false,
  heightAuto: false,
  scrollbarPadding: false,
  customClass: {
    title: 'swal-unsub-title',
    htmlContainer: 'swal-unsub-body'
  },
  didOpen: () => {
    // Add modal-open class to HTML tag when modal opens
    document.documentElement.classList.add('modal-open');
  },
  willClose: () => {
    // Remove modal-open class from HTML tag when modal closes
    document.documentElement.classList.remove('modal-open');
  }
};

/**
 * Normalizes any `href` values inside an HTML string so that
 * query parameters are rebuilt using URLSearchParams.
 *
 * This makes sure special characters like "+" in emails are encoded
 * the same way as in `useNavigation().toResendVerification`, e.g.:
 *   new URLSearchParams({ email }).toString()
 */
const normalizeHrefQueryParams = (html = '') => {
  if (typeof html !== 'string' || !html.includes('href=')) return html;

  return html.replace(/href=(['"])([^'"]+)\1/g, (fullMatch, quote, href) => {
    try {
      // Work on the href string manually to avoid '+' being treated as space.
      const hashIndex = href.indexOf('#');
      const queryIndex = href.indexOf('?');

      if (queryIndex === -1) {
        // No query params – leave as is.
        return fullMatch;
      }

      const base = href.substring(0, queryIndex);
      const hash = hashIndex !== -1 ? href.substring(hashIndex) : '';
      const queryString = href.substring(
        queryIndex + 1,
        hashIndex !== -1 ? hashIndex : href.length
      );

      const parts = queryString.split('&').map((part) => {
        const [rawKey, ...rawRest] = part.split('=');
        const key = rawKey;
        const rawValue = rawRest.join('='); // preserve original, including '+'

        if (!key) return part;

        // Specifically normalize the email param (and similar) via encodeURIComponent
        if (key === 'email' && rawValue) {
          return `${key}=${encodeURIComponent(rawValue)}`;
        }

        return part;
      });

      const newQuery = parts.join('&');
      const newHref = `${base}?${newQuery}${hash}`;

      return `href=${quote}${newHref}${quote}`;
    } catch (e) {
      // If parsing fails, keep the original href
      return fullMatch;
    }
  });
};

// Loading spinner HTML
const LOADING_SPINNER = `
  <div class="sweet_loader">
    <svg viewBox="0 0 140 140" width="70" height="70">
      <g class="outline">
        <path d="m 70 28 a 1 1 0 0 0 0 84 a 1 1 0 0 0 0 -84" 
              stroke="rgba(0,0,0,0.4)" 
              stroke-width="4" 
              fill="none" 
              stroke-linecap="round" 
              stroke-linejoin="round">
        </path>
      </g>
      <g class="circle">
        <path d="m 70 28 a 1 1 0 0 0 0 84 a 1 1 0 0 0 0 -84" 
              stroke="#f534bf" 
              stroke-width="4" 
              fill="none" 
              stroke-linecap="round" 
              stroke-linejoin="round" 
              stroke-dashoffset="200" 
              stroke-dasharray="300">
        </path>
      </g>
    </svg>
  </div>
`;

// ===========================================
// BASIC ALERT FUNCTIONS
// ===========================================

/**
 * Shows a success alert
 * @param {string} title - Alert title
 * @param {string} text - Alert message
 * @param {Object} options - Additional options
 */
export const showSuccess = async (title, text, options = {}) => {
  return await Swal.fire({
    title,
    text,
    icon: 'success',
    ...DEFAULT_CONFIG,
    ...options
  });
};

export const showSuccessWithLink = async (title, text, link, options = {}) => {
  // Check if the text contains HTML tags
  const hasHtmlTags = /<[^>]*>/g.test(text);

  // If HTML is present, normalize any href query params (for email links, etc.)
  const processedText = hasHtmlTags ? normalizeHrefQueryParams(text) : text;
  const processedLink = normalizeHrefQueryParams(link);

  // Use html property if HTML tags are detected, otherwise use text
  const alertConfig = {
    ...DEFAULT_CONFIG,
    title,
    icon: 'success',
    ...(hasHtmlTags ? { html: processedText } : { text: processedText }),
    ...options
  };

  return await Swal.fire(alertConfig);
};

/**
 * Shows an error alert
 * @param {string} title - Alert title
 * @param {string} text - Alert message
 * @param {Object} options - Additional options
 */
export const showError = async (title, text, options = {}) => {
  // Check if the text contains HTML tags
  const hasHtmlTags = /<[^>]*>/g.test(text);

  // If HTML is present, normalize any href query params (for email links, etc.)
  const processedText = hasHtmlTags ? normalizeHrefQueryParams(text) : text;

  // Use html property if HTML tags are detected, otherwise use text
  const alertConfig = {
    ...DEFAULT_CONFIG,
    title,
    icon: 'error',
    ...(hasHtmlTags ? { html: processedText } : { text: processedText }),
    ...options
  };

  return await Swal.fire(alertConfig);
};

/**
 * Shows a warning alert
 * @param {string} title - Alert title
 * @param {string} text - Alert message
 * @param {Object} options - Additional options
 */
export const showWarning = async (title, text, options = {}) => {
  return await Swal.fire({
    title,
    text,
    icon: 'warning',
    ...DEFAULT_CONFIG,
    ...options
  });
};

/**
 * Shows an info alert
 * @param {string} title - Alert title
 * @param {string} text - Alert message
 * @param {Object} options - Additional options
 */
export const showInfo = async (title, text, options = {}) => {
  return await Swal.fire({
    title,
    text,
    // icon: 'info',
    ...DEFAULT_CONFIG,
    ...options
  });
};

/**
 * Shows a question alert
 * @param {string} title - Alert title
 * @param {string} text - Alert message
 * @param {Object} options - Additional options
 */
export const showQuestion = async (title, text, options = {}) => {
  return await Swal.fire({
    title,
    text,
    icon: 'question',
    ...DEFAULT_CONFIG,
    ...options
  });
};

// ===========================================
// CONFIRMATION DIALOGS
// ===========================================

/**
 * Shows a yes/no confirmation dialog
 * @param {string} title - Dialog title
 * @param {string} text - Dialog message
 * @param {Object} options - Additional options
 * @returns {Promise<boolean>} - True if confirmed, false if cancelled
 */
export const confirmDialog = async (title, text, options = {}) => {
  const result = await Swal.fire({
    ...DEFAULT_CONFIG,
    title,
    text,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    ...options
  });

  return result.isConfirmed;
};

/**
 * Shows a custom confirmation dialog
 * @param {string} title - Dialog title
 * @param {string} text - Dialog message
 * @param {string} confirmText - Confirm button text
 * @param {string} cancelText - Cancel button text
 * @param {Object} options - Additional options
 * @param {string} options.imageUrl - Optional profile picture URL to show instead of icon
 * @returns {Promise<boolean>} - True if confirmed, false if cancelled
 */
export const customConfirm = async (title, text, confirmText = 'Yes', cancelText = 'No', options = {}) => {
  const { imageUrl, ...restOptions } = options;

  const config = {
    ...DEFAULT_CONFIG,
    title,
    text,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    ...restOptions
  };

  // If imageUrl is provided, use it instead of the default icon
  if (imageUrl) {
    config.imageUrl = imageUrl;
    config.imageWidth = 100;
    config.imageHeight = 100;
    config.imageAlt = 'Profile picture';
    // Add custom class for styling the image
    config.customClass = {
      ...config.customClass,
      image: 'swal-profile-image'
    };
  } else {
    config.icon = 'question';
  }

  const result = await Swal.fire(config);

  return result.isConfirmed;
};

/**
 * Shows a dangerous action confirmation (red confirm button)
 * @param {string} title - Dialog title
 * @param {string} text - Dialog message
 * @param {Object} options - Additional options
 * @returns {Promise<boolean>} - True if confirmed, false if cancelled
 */
export const confirmDangerousAction = async (title, text, options = {}) => {
  const result = await Swal.fire({
    ...DEFAULT_CONFIG,
    title,
    text,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#d33',
    ...options
  });

  return result.isConfirmed;
};

// ===========================================
// LOADING AND PROCESSING DIALOGS
// ===========================================

/**
 * Shows a loading dialog
 * @param {string} text - Loading message
 * @param {Object} options - Additional options
 * @returns {Promise} - Swal instance
 */
export const showLoading = (text = 'Loading...', options = {}) => {
  return Swal.fire({
    ...DEFAULT_CONFIG,
    title: text,
    html: LOADING_SPINNER,
    showConfirmButton: false,
    allowOutsideClick: false,
    ...options
  });
};

/**
 * Shows a processing payment dialog
 * @param {Object} options - Additional options
 * @returns {Promise} - Swal instance
 */
export const showPaymentProcessing = (options = {}) => {
  try {
    // Close any existing alert before opening a new one to prevent DOM conflicts
    if (Swal.isVisible()) {
      Swal.close();
    }
  } catch (error) {
    reportWarning(error, 'swal close existing alert');
  }

  return Swal.fire({
    ...DEFAULT_CONFIG,
    title: 'Processing payment...',
    html: LOADING_SPINNER,
    showConfirmButton: false,
    allowOutsideClick: false,
    ...options
  });
};

/**
 * Shows a loading payment dialog
 * @param {Object} options - Additional options
 * @returns {Promise} - Swal instance
 */
export const showPaymentLoading = (options = {}) => {
  return Swal.fire({
    ...DEFAULT_CONFIG,
    title: 'Loading payment...',
    html: LOADING_SPINNER,
    showConfirmButton: false,
    allowOutsideClick: false,
    ...options
  });
};

/**
 * Shows a loading free trial dialog
 * @param {Object} options - Additional options
 * @returns {Promise} - Swal instance
 */
export const showFreeTrialLoading = (options = {}) => {
  return Swal.fire({
    ...DEFAULT_CONFIG,
    title: 'Processing...',
    html: LOADING_SPINNER,
    showConfirmButton: false,
    allowOutsideClick: false,
    ...options
  });
};

/**
 * Shows a cancelling payment dialog
 * @param {Object} options - Additional options
 * @returns {Promise} - Swal instance
 */
export const showPaymentCancelling = (options = {}) => {
  return Swal.fire({
    ...DEFAULT_CONFIG,
    title: 'Cancelling payment...',
    html: LOADING_SPINNER + 'Cancelling the payment...',
    showConfirmButton: false,
    allowOutsideClick: false,
    ...options
  });
};

/**
 * Shows a file upload progress dialog
 * @param {number} progress - Upload progress (0-100)
 * @param {string} text - Progress text
 * @param {Object} options - Additional options
 * @returns {Promise} - Swal instance
 */
export const showUploadProgress = (progress, text = 'Uploading...', options = {}) => {
  return Swal.fire({
    ...DEFAULT_CONFIG,
    title: text,
    html: `
      <div style="text-align: center;">
        <div style="width: 100%; background-color: #f0f0f0; border-radius: 10px; margin: 20px 0;">
          <div style="width: ${progress}%; background-color: #f534bf; height: 20px; border-radius: 10px; transition: width 0.3s ease;"></div>
        </div>
        <p id="upload-progress-text" style="text-align: center; margin-top: 5px; color: #666; font-size: 14px;">${progress}%</p>
      </div>
    `,
    showConfirmButton: false,
    allowOutsideClick: false,
    ...options
  });
};

/**
 * Closes the current SweetAlert
 */
export const closeAlert = () => {
  try {
    if (Swal.isVisible()) {
      Swal.close();
    }
  } catch (error) {
    reportWarning(error, 'swal close alert');
  }
};

// ===========================================
// FILE VALIDATION AND UPLOAD ALERTS
// ===========================================

/**
 * Shows file size validation error
 * @param {string} fileName - Name of the file
 * @param {number} maxSizeInMB - Maximum allowed size in MB
 * @param {Object} options - Additional options
 */
export const showFileSizeError = async (fileName, maxSizeInMB, options = {}) => {
  return await showError(
    'File Too Large',
    `File "${fileName}" is too large. Maximum size is ${maxSizeInMB}MB.`,
    options
  );
};

/**
 * Shows file upload error
 * @param {string} message - Error message
 * @param {Object} options - Additional options
 */
export const showUploadError = async (message, options = {}) => {
  return await showError(
    'Upload Error',
    message || 'Failed to upload files. Please try again.',
    options
  );
};

/**
 * Shows upload success message
 * @param {string} message - Success message
 * @param {Object} options - Additional options
 */
export const showUploadSuccess = async (message, options = {}) => {
  return await showSuccess(
    'Uploaded!',
    message || 'Your content has been uploaded successfully.',
    options
  );
};

/**
 * Shows file type validation error
 * @param {string} allowedTypes - Allowed file types
 * @param {Object} options - Additional options
 */
export const showFileTypeError = async (allowedTypes, options = {}) => {
  return await showError(
    'Invalid File Type',
    `Please select a valid file type. Allowed types: ${allowedTypes}`,
    options
  );
};

// ===========================================
// PAYMENT AND SUBSCRIPTION ALERTS
// ===========================================

/**
 * Shows payment success message
 * @param {string} message - Success message
 * @param {Object} options - Additional options
 */
export const showPaymentSuccess = async (message, options = {}) => {
  return await showSuccess(
    'Success!',
    message || 'Payment processed successfully!',
    options
  );
};

/**
 * Shows payment error message
 * @param {string} message - Error message
 * @param {Object} options - Additional options
 */
export const showPaymentError = async (message, options = {}) => {
  return await showError(
    'Payment Failed',
    message || 'Payment failed. Please try again.',
    options
  );
};

/**
 * Shows subscription success message
 * @param {string} creatorName - Creator's name
 * @param {Object} options - Additional options
 */
export const showSubscriptionSuccess = async (creatorName, options = {}) => {
  return await showSuccess(
    'Subscription Confirmed 🎉',
    `You're officially subscribed to @${creatorName}. Send a message to start a conversation, request a call and explore their exclusive content.`,
    options
  );
};

/**
 * Shows unsubscribe confirmation
 * @param {string} creatorName - Creator's name
 * @param {Object} options - Additional options
 * @param {string} options.imageUrl - Optional profile picture URL to show instead of icon
 * @returns {Promise<boolean>} - True if confirmed, false if cancelled
 */
export const confirmUnsubscribe = async (creatorName, options = {}) => {
  return await customConfirm(
    '',
    `Unsubscribe from ${creatorName}?`,
    'Unsubscribe',
    'Cancel',
    options
  );
};

/**
 * Shows follow success message
 * @param {string} username - Username
 * @param {Object} options - Additional options
 */
export const showFollowSuccess = async (username, options = {}) => {
  return await showSuccess(
    'Success!',
    `You are now following @${username}.`,
    options
  );
};

/**
 * Shows unfollow success message
 * @param {string} username - Username
 * @param {Object} options - Additional options
 */
export const showUnfollowSuccess = async (username, options = {}) => {
  return await showSuccess(
    'Success!',
    `You have unfollowed @${username}.`,
    options
  );
};

/**
 * Shows unfollow confirmation
 * @param {string} username - Username
 * @param {Object} options - Additional options
 * @param {string} options.imageUrl - Optional profile picture URL to show instead of icon
 * @returns {Promise<boolean>} - True if confirmed, false if cancelled
 */
export const confirmUnfollow = async (username, options = {}) => {
  return await customConfirm(
    '',
    `Unfollow ${username}?`,
    'Unfollow',
    'Cancel',
    options
  );
};

// ===========================================
// AUTHENTICATION AND USER ACTION ALERTS
// ===========================================

/**
 * Shows sign-in required alert
 * @param {string} action - Action that requires sign-in
 * @param {Object} options - Additional options
 * @returns {Promise<boolean>} - True if user wants to Login, false otherwise
 */
export const showSignInRequired = async (action = 'perform this action', options = {}) => {
  const result = await Swal.fire({
    // title: 'Log in or Sign up to Required',
    ...DEFAULT_CONFIG,
    text: `Log in or Sign up to ${action}`,
    showCancelButton: true,
    confirmButtonText: 'Login',
    cancelButtonText: 'Cancel',
    ...options
  });

  return result.isConfirmed;
};

/**
 * Shows follow required alert
 * @param {string} action - Action that requires follow
 * @param {Object} options - Additional options
 * @returns {Promise<boolean>} - True if user wants to follow, false otherwise
 */
export const showFollowRequired = async (action = 'perform this action', options = {}) => {
  const result = await Swal.fire({
    ...DEFAULT_CONFIG,
    title: 'Chat Locked 🔒',
    text: `Follow to ${action}.`,
    showCancelButton: true,
    confirmButtonText: 'Follow',
    cancelButtonText: 'Cancel',
    ...options
  });

  return result.isConfirmed;
};

/**
 * Shows subscription required alert
 * @param {string} action - Action that requires subscription
 * @param {Object} options - Additional options
 * @returns {Promise<boolean>} - True if user wants to subscribe, false otherwise
 */
export const showSubscriptionRequired = async (action = 'perform this action', options = {}) => {
  const result = await Swal.fire({
    ...DEFAULT_CONFIG,
    title: 'Chat Locked 🔒',
    text: `Subscribe to ${action}.`,
    showCancelButton: true,
    confirmButtonText: 'Subscribe',
    cancelButtonText: 'Cancel',
    ...options
  });

  return result.isConfirmed;
};

// ===========================================
// CONTENT AND INTERACTION ALERTS
// ===========================================

/**
 * Shows content purchase success
 * @param {Object} options - Additional options
 */
export const showContentPurchaseSuccess = async (options = {}) => {
  return await showSuccess(
    'Success!',
    'Content purchased successfully!',
    options
  );
};

/**
 * Shows PPV content unlocked success (after purchase)
 * @param {Object} options - Additional options
 */
export const showContentUnlockedSuccess = async (options = {}) => {
  return await showSuccess(
    'Content Unlocked 🎉',
    'You now have access to this exclusive content.',
    options
  );
};

/**
 * Shows tip sent success
 * @param {Object} options - Additional options
 */
export const showTipSentSuccess = async (options = {}) => {
  return await showSuccess(
    'Tip Sent 🎉',
    'Your tip was delivered! Feeling generous? Make it even juicier to get noticed.',
    options
  );
};

/**
 * Shows report sent success
 * @param {Object} options - Additional options
 */
export const showReportSentSuccess = async (options = {}) => {
  return await showSuccess(
    'Report Sent',
    'Your report has been submitted successfully.',
    options
  );
};

/**
 * Shows message sent success
 * @param {Object} options - Additional options
 */
export const showMessageSentSuccess = async (options = {}) => {
  return await showSuccess(
    'Message Sent',
    'Your message has been sent successfully.',
    options
  );
};

// ===========================================
// CONTACT AND SUPPORT ALERTS
// ===========================================

/**
 * Shows contact form success
 * @param {Object} options - Additional options
 */
export const showContactFormSuccess = async (options = {}) => {
  return await showSuccess(
    'Request Submitted',
    'Thank you for contacting us. We will get back to you as soon as possible.',
    options
  );
};

/**
 * Shows contact form error
 * @param {string} message - Error message
 * @param {Object} options - Additional options
 */
export const showContactFormError = async (message, options = {}) => {
  return await showError(
    'Error',
    message || 'Failed to submit your request. Please try again.',
    options
  );
};

// ===========================================
// PROFILE AND SETTINGS ALERTS
// ===========================================

/**
 * Shows profile update success
 * @param {string} field - Updated field name
 * @param {Object} options - Additional options
 */
export const showProfileUpdateSuccess = async (field, options = {}) => {
  return await showSuccess(
    'Success!',
    `${field} updated successfully`,
    options
  );
};

/**
 * Shows profile update error
 * @param {string} field - Field name
 * @param {string} message - Error message
 * @param {Object} options - Additional options
 */
export const showProfileUpdateError = async (field, message, options = {}) => {
  return await showError(
    'Error!',
    message || `${field} update failed. Please try again.`,
    options
  );
};

// ===========================================
// UTILITY FUNCTIONS
// ===========================================

/**
 * Shows a custom alert with HTML content
 * @param {string} title - Alert title
 * @param {string} html - HTML content
 * @param {Object} options - Additional options
 */
export const showCustomAlert = async (title, html, options = {}) => {
  return await Swal.fire({
    title,
    html,
    ...DEFAULT_CONFIG,
    ...options
  });
};

/**
 * Shows a custom alert with React JSX content
 * @param {string} title - Alert title (optional)
 * @param {React.ReactNode} reactContent - React JSX content
 * @param {Object} options - Additional options
 * @returns {Promise} - Swal instance
 */
export const showCustomAlertWithReact = async (title, reactContent, options = {}) => {
  const config = {
    ...DEFAULT_CONFIG,
    ...options
  };

  if (title) {
    config.title = title;
  }

  return await SwalReact.fire({
    ...config,
    html: reactContent
  });
};

/**
 * Shows a toast notification
 * @param {string} message - Toast message
 * @param {string} type - Toast type (success, error, warning, info)
 * @param {Object} options - Additional options
 */
export const showToast = (message, type = 'success', options = {}) => {
  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    ...options
  });

  return Toast.fire({
    icon: type,
    title: message
  });
};

/**
 * Shows a timer-based alert that auto-closes
 * @param {string} title - Alert title
 * @param {string} text - Alert message
 * @param {number} timer - Auto-close timer in milliseconds
 * @param {Object} options - Additional options
 */
export const showTimedAlert = async (title, text, timer = 2000, options = {}) => {
  return await Swal.fire({
    ...DEFAULT_CONFIG,
    title,
    text,
    timer,
    showConfirmButton: false,
    ...options
  });
};

// ===========================================
// EXPORT DEFAULT CONFIG FOR CUSTOMIZATION
// ===========================================

export { DEFAULT_CONFIG, LOADING_SPINNER };

// ===========================================
// GLOBAL SWEETALERT CONFIGURATION
// ===========================================

// Export Swal instance for advanced usage
export { Swal };
